import * as React from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelectedRows, useSelectedRowsDispatch, useModalContext } from "common/contexts";
import BulkActionsDropdown from "components/common/EntityDataTable/BulkActionsDropdown";
import { MenuItem, Button } from "components/bootstrap";
import PacksModals from "security-app/components/common/ThreatCoverageWidget/ImproveCoverageModal/PacksModals";
import usePermissions from "hooks/usePermissions";
import ClearSelection from "./clear-selection";
import ExpandedPackDetails from "./expanded-pack-details";
const TableBulkActions = () => {
  const { t } = useTranslation("enterprise_illuminate");
  const { setModal, setEntity } = useModalContext();
  const selectedPacks = useSelectedRows();
  const onDisable = () => {
    setEntity("disable");
    setModal("CONFIRM_DISABLE_PACKS");
  };
  const onEnable = () => {
    setEntity("enable");
    setModal("CONFIRM_ENABLE_PACKS");
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(BulkActionsDropdown, { bsSize: "xs" }, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: onEnable, disabled: selectedPacks.length < 1 }, t("table_elements.enable_packs")), /* @__PURE__ */ React.createElement(MenuItem, { onSelect: onDisable, disabled: selectedPacks.length < 1 }, t("table_elements.disable_packs"))), /* @__PURE__ */ React.createElement(PacksModals, null));
};
const PackAction = ({ pack }) => {
  const { t } = useTranslation("enterprise_illuminate");
  const selectedPacksDispatch = useSelectedRowsDispatch();
  const { setModal, setEntity } = useModalContext();
  const addPackToSelectedRows = useCallback(
    () => selectedPacksDispatch({ type: "upsert", payload: [pack] }),
    [pack, selectedPacksDispatch]
  );
  const onDisable = () => {
    addPackToSelectedRows();
    setEntity("disable");
    setModal("CONFIRM_DISABLE_SINGLE_PACK");
  };
  const onEnable = () => {
    addPackToSelectedRows();
    setEntity("enable");
    setModal("CONFIRM_ENABLE_SINGLE_PACK");
  };
  return pack.enabled ? /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: onDisable, bsStyle: "danger" }, t("table_elements.disable")) : /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", onClick: onEnable }, t("table_elements.enable"));
};
function useTableElements(withTableBulkActions = false) {
  const selectedPacks = useSelectedRows();
  const { isPermitted } = usePermissions();
  const isPermittedToDoActions = useMemo(() => isPermitted("illuminate_bundle_management:install"), [isPermitted]);
  const selectedPacksDispatch = useSelectedRowsDispatch();
  const renderExpandedRow = useCallback((pack) => /* @__PURE__ */ React.createElement(ExpandedPackDetails, { pack }), []);
  const expandedSections = useMemo(
    () => ({
      restFieldsExpandedSection: {
        title: null,
        content: renderExpandedRow
      }
    }),
    [renderExpandedRow]
  );
  const bulkSelection = useMemo(
    () => ({
      onChangeSelection: (packIds, list) => {
        const auxSelectedPacks = packIds.length > 0 ? packIds.map((packId) => [...selectedPacks, ...list].find((pack) => pack.id === packId)) : [];
        selectedPacksDispatch({ type: "upsert", payload: auxSelectedPacks });
      },
      actions: withTableBulkActions ? /* @__PURE__ */ React.createElement(TableBulkActions, null) : /* @__PURE__ */ React.createElement(ClearSelection, null),
      isEntitySelectable: (pack) => pack.is_compatible && pack.included_in_bundle
    }),
    [selectedPacks, selectedPacksDispatch, withTableBulkActions]
  );
  const entityActions = useCallback((pack) => /* @__PURE__ */ React.createElement(PackAction, { pack }), []);
  if (!isPermittedToDoActions)
    return {
      expandedSections
    };
  return {
    expandedSections,
    bulkSelection,
    bulkActions: /* @__PURE__ */ React.createElement(ClearSelection, null),
    entityActions
  };
}
export default useTableElements;
